import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCoreRowModel, Row, useReactTable } from '@tanstack/react-table';

import { JsonDataViewer, ServerTableLayout } from 'shared/ui';

import { ManualCommand } from 'entities/manual-command';

import { useColumns } from '../hooks/use-columns';
import {
  useFiltersState,
  usePaginationState,
  useSortingState,
} from 'shared/ui/table/hooks';

type Props = {
  tableData: ManualCommand[];
  loading: boolean;
  count: number | undefined;
  tablePageSize: number;
  tableHeadRef: (element: HTMLElement | SVGElement | null) => void;
  groupId?: string;
};

export function TableInstance({
  tableData,
  loading,
  count,
  tableHeadRef,
  tablePageSize,
}: Props) {
  const [commandToShowInJSON, setCommandToShowInJSON] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();

  const columns = useColumns();

  const navigate = useNavigate();

  const ordering = searchParams.get('ordering');
  const type = searchParams.get('type');
  const page = searchParams.get('page');
  const limit = searchParams.get('limit');
  const search = searchParams.get('search');
  const searchText = searchParams.get('searchText');

  const data = useMemo(() => tableData, [tableData]);

  const sorting = useSortingState();
  const pagination = usePaginationState();
  const filters = useFiltersState();

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters: filters,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: count
      ? Math.ceil(count / (limit ? Number(limit) : tablePageSize))
      : undefined,
  });

  const handleRowDoubleClick = (row: Row<ManualCommand>) => {
    setCommandToShowInJSON(row.original.transactionId);
  };

  if (commandToShowInJSON) {
    const commandJSON = tableData.find(
      (command) => command.transactionId === commandToShowInJSON
    ) as ManualCommand;

    return (
      <JsonDataViewer
        data={commandJSON}
        onClick={() => setCommandToShowInJSON('')}
      />
    );
  }

  return (
    <ServerTableLayout
      table={table}
      loading={loading}
      pageSize={tablePageSize}
      tableHeadRef={tableHeadRef}
      handleRowDoubleClick={handleRowDoubleClick}
      rowsCount={count}
    />
  );
}
